import React, { useState, useEffect } from "react";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton, Link, Paper, Tooltip, Typography, CircularProgress } from "@mui/material";
import { CloudDownloadOutlined, CompareArrows, HelpOutline, HighlightOff, MenuBookOutlined, GetApp } from "@mui/icons-material";
import { SectionTitle } from "../../../export/advanced_export";
import EnhancedSelect from "../../../../../../components/EnhancedSelect";
import DropZone from "../../../../../files/dropzone";
import EnhancedProfileOrig from "../../../export/components/EnhancedProfile";
import NavigationButtons from "./NavigationButtons";
import { SaveAsDialog, saveAsSharedProfile } from "../../../../../files/import_export_profiles";
import EnhancedTextField from "../../../../../../components/EnhancedTextField";
import Config from "../../../../../../config";
import { getRetailerCategoryTemplate, SUPPLIER_SYNDICATION_ENABLED } from "../../../../../../utils/utils";
import Helpers from "../../../../../../helpers";
import axios from "axios";
import UpdatingDialog from "../../../../../../components/Dialogs/UpdatingDialog";
import FileSaver from 'filesaver.js-npm';
import { DialogHeader, pageContentGap } from "../../../../../../theme";
import type { WizardSharedProps } from "../types";
const EnhancedProfile = EnhancedProfileOrig as any;

// region TypeScript Interfaces
type ProductImportProps = WizardSharedProps;
// endregion

export default function ProductImport(props: ProductImportProps) {
    const { allowBack, allowNext, backStep, nextStep, activeStep, steps } = props;
    const { profile, setProfile, profileType, availableProfiles, blankProfile, loadImportProfiles } = props;
    const { formattedProfile, formatProfileFromRemote } = props;
    const { file, onUploadDrop } = props;
    const { partners } = props;
    const { loading, loadingPreviousImport, loadingPreviousFiles } = props;
    const { setError } = props;
    const { storageProfileName, storageHelper } = props;

    const isMarketplace = props.retailers && props.retailers.some((retailer: any) => retailer.Marketplace === true);

    const linkSx = (theme: any) => ({
        color: theme.palette.tertiary?.main || theme.palette.primary.main,
        textDecoration: "none",
        "&:hover": { textDecoration: "underline" }
    });

    const [saveAsDialogOpen, setSaveAsDialogOpen] = useState<boolean>(false);
    const [downloadingTemplate, setDownloadingTemplate] = useState<boolean>(false);
    const [selectedFileUrl, setSelectedFileUrl] = useState<string | null>(null);
    const [selectedFileName, setSelectedFileName] = useState<string | null>(null);
    const [downloadingFile, setDownloadingFile] = useState<boolean>(false);

    // Initialize selected file URL when file prop changes
    useEffect(() => {
        if (file && file.name && props.previousImports) {
            // Try to find the matching file URL from previousImports
            for (const importGroup of props.previousImports) {
                for (const record of importGroup.Records as any[]) {
                    if (record.Description === file.name) {
                        const parsedFile = record.BlobPath.split("/");
                        const fileName = parsedFile[parsedFile.length - 1];
                        const url = Config.api + "/api/v1/attachments/catalogmanagerfiles/" + fileName;
                        setSelectedFileUrl(url);
                        setSelectedFileName(file.name);
                        break;
                    }
                }
            }
        }
    }, [file, props.previousImports]);

    const onNext = () => {
        // Remove any invalid TargetField mappings if there doesn't exist a retailer attribute for it //
        let validAttributeMappings: Record<string, any> = {};
        for (let [key, value] of Object.entries(profile.AttributeMappings)) {
            if (props.retailerAttributes.find((attribute: any) => attribute.Name === key) !== undefined) {
                validAttributeMappings[key] = value;
            }
        }
        setProfile((prevState) => {
            return {
                ...prevState,
                AttributeMappings: validAttributeMappings
            }
        });
    }

    const onProfileSave = async (newName: string) => {
        await new Promise(r => setTimeout(r, 750));
        storageHelper.set(storageProfileName, newName);
        const loadedProfiles = await loadImportProfiles();
        const foundProfile = loadedProfiles.find((loadedProfile: any) => loadedProfile.Name === newName);
        setProfile({ ...foundProfile } as any);
        setSaveAsDialogOpen(false);
    }

    const onSelectNewDataSource = async (e: any) => {
        if (e == null) {
            setSelectedFileUrl(null);
            setSelectedFileName(null);
            return;
        }
        else {
            const url = e.value;
            setSelectedFileUrl(url);
            setSelectedFileName(e.name);

            const downloadResponse = await axios.get(url, { responseType: 'blob' });

            if (downloadResponse.status !== 200) {
                setError({ open: true, errorHeader: "Download Error", errorMessage: "Failed to download file. " });
                return;
            }

            let file = new File([downloadResponse.data], e.name);
            await onUploadDrop([file]);
        }
    }

    const handleDownloadSelectedFile = async () => {
        if (!selectedFileUrl || !selectedFileName) return;

        setDownloadingFile(true);
        try {
            const response = await axios.get(selectedFileUrl, { responseType: 'blob' });
            if (response.status === 200) {
                (FileSaver as any).saveAs(response.data, selectedFileName);
            } else {
                setError({ open: true, errorHeader: "Download Error", errorMessage: "Failed to download the selected file." });
            }
        } catch (error) {
            console.error('Download error:', error);
            setError({ open: true, errorHeader: "Download Error", errorMessage: "An error occurred while downloading the file." });
        } finally {
            setDownloadingFile(false);
        }
    }

    return (
        <Grid container style={{ gap: pageContentGap }}>
            {(!props.isRetailer || (props.isRetailer && (profile.Partner || props.urlPartnerId))) && (
                <Paper style={{ width: "100%", padding: "24px", borderRadius: "5px" }}>
                    <Grid container style={{ flexDirection: "column", alignItems: "flex-start" }}>
                        <SectionTitle style={{ marginBottom: "24px" }}>
                            {
                                (profile.Partner !== "" && props.isRetailer)
                                    ? "Select Supplier"
                                    : props.isRetailer
                                        ? "Select Supplier"
                                        : isMarketplace && SUPPLIER_SYNDICATION_ENABLED
                                            ? "Select Retailer/Channel"
                                            : "Select Retailer"
                            }
                        </SectionTitle>

                        <Grid container justifyContent={"space-between"} style={{ width: "100%" }}>
                            <EnhancedSelect
                                isClearable
                                loading={loading}
                                value={{
                                    value: profile.Partner,
                                    label: partners.find((partner: any) => partner.Id === profile.Partner)?.CompanyName ?? ""
                                }}
                                options={partners.map((partner: any) => ({
                                    value: partner.Id,
                                    label: partner.CompanyName
                                }))}
                                onChange={(e: any) => {
                                    if (e == null) {
                                        setProfile({ ...blankProfile, Partner: "" });
                                        return;
                                    }

                                    setProfile({ ...blankProfile, Partner: e.value });
                                    // Set partnerId in URL
                                    if (props.isRetailer && props.urlPartnerId) {
                                        new (Helpers as any)().setUrlObject('partnerId', e.value, props.history, props.location);
                                    }

                                    let availableProfilesForPartner = availableProfiles.filter((avalProfile: any) => avalProfile.Partner === e.value);
                                    if (availableProfilesForPartner.length === 0) {
                                        setSaveAsDialogOpen(true);
                                    } else {
                                        let selectedProfile = availableProfilesForPartner[0];
                                        if (selectedProfile !== undefined) {
                                            setProfile({ ...profile, ...selectedProfile });
                                            storageHelper.set(storageProfileName, selectedProfile.Name);
                                        }
                                    }
                                }}
                            />
                        </Grid>
                    </Grid>
                </Paper>
            )}

            <Paper style={{ width: "100%", padding: "24px", borderRadius: "5px" }}>
                <Grid container style={{flexDirection: "column", alignItems: "flex-start"}}>
                    <SectionTitle style={{ marginBottom: "24px" }}>
                        Upload Instructions
                    </SectionTitle>

                    <Grid container justifyContent={"space-between"} style={{ flexWrap: "nowrap", width: "100%", gap: "5px" }}>
                        <Tooltip title={"Select a retailer below to get a retailer specific template."} arrow>
                            <Grid item style={{ display: "flex", gap: "8px" }} size={3}>
                                <CloudDownloadOutlined
                                    style={{ color: "#666666" }}
                                />
                                <Typography style={{ textAlign: "left" }}>
                                    Import your products using the standard template
                                    <br />
                                    <Link sx={linkSx}
                                        style={{ textAlign: "left" }}
                                        href={profile.Partner === undefined || profile.Partner === "" ? "/product-feeds" : "#"}
                                        target={profile.Partner === undefined || profile.Partner === "" ? "_blank" : undefined}
                                        rel={profile.Partner === undefined || profile.Partner === "" ? "noopener" : undefined}
                                        onClick={async () => {
                                            if (profile.Partner === undefined || profile.Partner === "") {
                                                return;
                                            }

                                            setDownloadingTemplate(true);
                                            await (getRetailerCategoryTemplate as any)(profile.Partner, "");
                                            setDownloadingTemplate(false);
                                        }}
                                    >
                                        Download the template
                                    </Link>
                                </Typography>
                            </Grid>
                        </Tooltip>
                        <Grid item style={{ display: "flex", gap: "8px" }} size={3}>
                            <MenuBookOutlined
                                style={{ color: "#666666" }}
                            />
                            <Typography style={{ textAlign: "left" }}>
                                Explore our Help Center articles
                                <br />
                                <Link sx={linkSx} href={"https://help.logicbroker.com"}
                                    target="_blank" rel="noopener" style={{ textAlign: "left" }}>
                                    Help Center
                                </Link>
                            </Typography>
                        </Grid>
                        <Grid item style={{ display: "flex", gap: "8px" }} size={3}>
                            <CompareArrows
                                style={{ color: "#666666" }} />
                            <Typography style={{ textAlign: "left" }}>
                                Import products using API
                                <br />
                                <Link sx={linkSx} href={Config.api + "/swagger/ui/index#/ProductOnboardingCenter"} target="_blank"
                                    rel="noopener" style={{ textAlign: "left" }}>
                                    API Documentation
                                </Link>
                            </Typography>
                        </Grid>
                        <Grid item style={{ display: "flex", gap: "8px" }} size={3}>
                            <HelpOutline
                                style={{ color: "#666666" }} />
                            <Typography style={{ textAlign: "left" }}>
                                View required fields
                                <br />
                                <Link sx={linkSx}
                                    href={`/product-feeds${profile.Partner !== "" ? `/specification?partner=${profile.Partner}` : ""}`}
                                    target="_blank" rel="noopener"
                                    style={{ textAlign: "left" }}>
                                    {!props.isRetailer
                                        ? isMarketplace && SUPPLIER_SYNDICATION_ENABLED
                                            ? "Retailer/Channel"
                                            : "Retailers"
                                        : ""
                                    } Specification
                                </Link>
                            </Typography>
                        </Grid>
                    </Grid>
                </Grid>
            </Paper>

            <Paper style={{ width: "100%", padding: "24px", borderRadius: "5px" }}>
                <Grid container>
                    <Grid container flexDirection={"column"} alignItems={"flex-start"} style={{width: "100%", gap: "5px"}}>
                        <EnhancedProfile
                            title={"Mapping Profile"}
                            allowNew={true}
                            disabled={(
                                (!props.isRetailer && (profile.Partner === undefined || profile.Partner === ""))
                            )}

                            isRetailer={props.isRetailer}

                            sharedByDefault={true}

                            profile={formattedProfile as any} setProfile={setProfile as any}
                            profileType={profileType}

                            availableProfiles={
                                (profile.Partner === undefined || profile.Partner === ""
                                    ? availableProfiles.filter((avalProfile: any) => avalProfile.Partner === "" || avalProfile.Partner == null)
                                    : availableProfiles.filter((avalProfile: any) => avalProfile.Partner === profile.Partner)) as any
                            }

                            blankProfile={{ ...blankProfile, Partner: profile.Partner } as any} loadProfiles={loadImportProfiles as any}

                            profilePreMap={formatProfileFromRemote as any}

                            setError={setError as any}
                            loading={loading}
                            stoageProfileName={storageProfileName} storageHelper={storageHelper}
                            operationType={"import"}
                        />
                    </Grid>
                </Grid>
            </Paper>

            <Paper style={{ width: "100%", padding: "24px", borderRadius: "5px" }}>
                <Grid container style={{flexDirection: "column", alignItems: "flex-start"}}>
                    <Grid container style={{ justifyContent: "center", alignItems: "flex-start", gap: "10px", marginBottom: "10px", flexDirection: "column", width: "100%" }}>
                        <Grid container style={{ gap: "10px", alignItems: "center", width: "100%" }}>
                            <SectionTitle>
                                Data Source:
                            </SectionTitle>

                            <EnhancedSelect
                                loading={loadingPreviousFiles}
                                value={{
                                    value: file?.name ?? "",
                                    label: file?.name ?? "",
                                    name: file?.name ?? ""
                                }}
                                options={
                                    props.previousImports.map((importGroup: any) => {
                                        return importGroup.Records.map((record: any) => {
                                            const parsedFile = record.BlobPath.split("/");
                                            const fileName = parsedFile[parsedFile.length - 1];
                                            return {
                                                name: record.Description,
                                                label: record.Description + " - " + record.PartnerCompanyName,
                                                url: Config.api + "/api/v1/attachments/catalogmanagerfiles/" + fileName
                                            }
                                        });
                                    }).flat().map((record: any) => ({
                                        value: record.url,
                                        label: record.label,
                                        name: record.name
                                    }))
                                }
                                onChange={onSelectNewDataSource}
                            />

                            <Tooltip title={selectedFileUrl ? "Download selected file" : "Select a file to download"} arrow>
                                <span>
                                    <IconButton
                                        onClick={handleDownloadSelectedFile}
                                        disabled={!selectedFileUrl || downloadingFile}
                                        style={{
                                            padding: "8px",
                                            backgroundColor: selectedFileUrl && !downloadingFile ? "#f5f5f5" : undefined
                                        }}
                                        size="large">
                                        {downloadingFile ? <CircularProgress size={20} /> : <GetApp />}
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </Grid>

                        <Typography>
                            Or
                        </Typography>

                        <SectionTitle>
                            Upload File
                        </SectionTitle>
                    </Grid>

                    <Grid container style={{width: "100%"}}>
                        <DropZone
                            accept=".csv, .xlsx, .txt"
                            style={{ width: '100%', height: '10em', border: "1px dashed #93AAFD", borderRadius: "4px" }}
                            onDrop={onUploadDrop}
                            disableClick={
                                props.isRetailer
                                    ? (profile.Name === undefined || profile.Name === "")
                                    : (profile.Partner === undefined || profile.Partner === "" || profile.Name === undefined || profile.Name === "")
                            }
                        >
                            {({ isDragAccept, isDragReject, acceptedFiles, rejectedFiles }: any) => {
                                if (isDragAccept || isDragReject) {
                                    return "Drop here to upload this file.";
                                }

                                let message = "";
                                if (!props.isRetailer) {
                                    if (profile.Partner === undefined || profile.Partner === "") {
                                        message = "Please select a retailer to continue.";
                                    }
                                    else if (profile.Name === undefined || profile.Name === "") {
                                        message = "Please select a profile to continue.";
                                    }
                                    else {
                                        message = 'Drop a CSV/XLSX file here or click to choose a file.';
                                    }
                                }
                                else {
                                    if (props.urlPartnerId && (profile.Partner === undefined || profile.Partner === "")) {
                                        message = "Please select a supplier to continue.";
                                    }
                                    else if (profile.Name === undefined || profile.Name === "") {
                                        message = "Please select a profile to continue.";
                                    }
                                    else {
                                        message = 'Drop a CSV/XLSX file here or click to choose a file.';
                                    }
                                }

                                return file
                                    ? loadingPreviousImport ? "Parsing input file/loading previous import, please wait..." : file.name
                                    : message;
                            }}
                        </DropZone>
                    </Grid>
                </Grid>
            </Paper>

            {/*region Extra Actions*/}
            <NavigationButtons
                allowBack={allowBack}
                allowNext={allowNext}
                backStep={backStep}
                nextStep={nextStep}
                onNext={onNext}
                activeStep={activeStep}
                steps={steps}

                loading={loading || loadingPreviousImport}

                formattedProfile={formattedProfile}
                profileType={profileType}
            />
            <SaveAsDialog open={saveAsDialogOpen} profileType={profileType} values={formattedProfile}
                onClose={() => setSaveAsDialogOpen(false)}
                onSaveAs={async (newName: string) => {
                    onProfileSave(newName);
                }}
                template={(saveAsProps: any) => (
                    <Dialog
                        open={saveAsProps.open}
                        onClose={(event: object, reason: string) => {
                            if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
                            saveAsProps.onClose(event, reason);
                        }}
                        maxWidth='sm'
                        fullWidth={true}
                        slotProps={{
                            paper: {
                                style: {
                                    borderRadius: "12px"
                                }
                            }
                        }}>
                        <DialogTitle>
                            <Grid item container justifyContent={"space-between"} alignItems={"center"}>
                                <Grid item container style={{ flex: 1, flexDirection: "column" }}>
                                    <DialogHeader>
                                        Save New Profile As
                                    </DialogHeader>

                                    <Typography style={{ fontSize: "16px", fontWeight: 400 }}>
                                        No profiles found for this {props.isRetailer ? "supplier" : "retailer"}, please create a new one.
                                    </Typography>
                                </Grid>

                                <IconButton
                                    onClick={() => {
                                        saveAsProps.onClose();
                                        setProfile({ ...blankProfile, Partner: "000000" });
                                        alert("To continue you will need to select a partner and create a profile if none are available.");
                                    }}
                                    size="large">
                                    <HighlightOff style={{ color: "red" }} />
                                </IconButton>
                            </Grid>
                        </DialogTitle>

                        <DialogContent>
                            <EnhancedTextField
                                placeholder={"Enter a name for the new profile"}
                                value={saveAsProps.state.newProfileName}
                                onChange={(e: any) => saveAsProps.setState({ newProfileName: e.target.value })}
                            />
                        </DialogContent>
                        <DialogActions style={{ padding: "24px" }}>
                            <Button variant="text" color="primary"
                                disabled={!saveAsProps.state.newProfileName || saveAsProps.state.disabled}
                                onClick={async () => {
                                    let newProfile = { ...formattedProfile, Name: saveAsProps.state.newProfileName };
                                    await saveAsProps.saveAs();
                                    await (saveAsSharedProfile as any)(newProfile, profileType, false);
                                    await onProfileSave(newProfile.Name as string);
                                }}>
                                Save
                            </Button>
                        </DialogActions>
                    </Dialog>
                )}
            />
            <UpdatingDialog
                open={downloadingTemplate}
                title={"Downloading Template"}
                subtitle={"Please wait while we download the template."}
            />
            {/*endregion*/}
        </Grid>
    );
}
